// Estados compartilhados entre /phone e /dialer: skeleton da área de trabalho
// enquanto o bundle/ramal carrega, aviso honesto de ramal indisponível e aviso
// de que a tela pode bloquear e derrubar a ligação (wake lock sem garantia).
import { CircleAlert, TriangleAlert } from "lucide-react";
import { useTranslation } from "react-i18next";
import "../i18n.js";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Spinner } from "@/components/ui/spinner";
import { detectPlatform } from "@/hooks/use-install-prompt.js";
import { cn } from "@/lib/utils";
import type { WakeLockStatus } from "@/sip/use-wake-lock.js";

type PhoneWorkspaceVariant = "dialer" | "phone";

function PanelHeaderSkeleton() {
  return (
    <div className="flex items-center gap-3">
      <Skeleton className="size-8 shrink-0 rounded-lg" />
      <Skeleton className="h-4 w-32" />
    </div>
  );
}

function ScriptPanelSkeleton() {
  return (
    <div className="flex min-h-[25rem] min-w-0 flex-1 flex-col rounded-2xl border bg-card/80 p-5 shadow-sm lg:min-h-0">
      <PanelHeaderSkeleton />
      <Skeleton className="mt-3 h-3 w-3/4" />
      <div className="mt-7 space-y-5">
        {["w-2/3", "w-3/4", "w-1/2"].map((width, index) => (
          <div key={width} className="rounded-xl border border-border/70 p-4">
            <div className="flex items-center gap-3">
              <Skeleton className="size-7 rounded-full" />
              <Skeleton className={cn("h-3.5", width)} />
            </div>
            <Skeleton className="mt-4 h-3 w-full" />
            <Skeleton className="mt-2 h-3 w-5/6" />
            {index === 0 && <Skeleton className="mt-2 h-3 w-3/5" />}
          </div>
        ))}
      </div>
    </div>
  );
}

function SessionPanelSkeleton({ variant }: { variant: PhoneWorkspaceVariant }) {
  return (
    <div className="flex min-h-[22rem] min-w-0 flex-1 flex-col rounded-2xl border bg-card/80 p-5 shadow-sm lg:min-h-0">
      <div className="flex items-center gap-3">
        <Skeleton className="size-8 shrink-0 rounded-lg" />
        <Skeleton className="h-4 w-32" />
        {variant === "dialer" && <Skeleton className="ml-auto h-5 w-14 rounded-full" />}
      </div>
      <Skeleton className="mt-3 h-3 w-2/3" />
      <div className="mt-6 space-y-3">
        {Array.from({ length: variant === "dialer" ? 5 : 4 }).map((_, index) => (
          <div
            key={index}
            className="flex items-center gap-3 rounded-xl border border-border/60 p-3"
          >
            <Skeleton className="size-9 shrink-0 rounded-full" />
            <div className="min-w-0 flex-1 space-y-2">
              <Skeleton className={cn("h-3.5", index % 2 === 0 ? "w-2/3" : "w-1/2")} />
              <Skeleton className="h-2.5 w-4/5" />
            </div>
            <Skeleton className="h-5 w-12 rounded-full" />
          </div>
        ))}
      </div>
    </div>
  );
}

function PhoneColumnSkeleton() {
  return (
    <div className="mx-auto flex min-h-[34rem] w-full max-w-[400px] flex-col rounded-xl bg-card px-4 py-4 ring-1 ring-foreground/10 lg:mx-0 lg:min-h-0">
      <div className="grid h-12 grid-cols-2 gap-1 rounded-xl bg-muted/45 p-1">
        <Skeleton className="h-full bg-background/80 shadow-sm" />
        <Skeleton className="h-full bg-muted-foreground/10" />
      </div>
      <div className="mt-4 flex min-h-0 flex-1 flex-col">
        <div className="overflow-hidden rounded-2xl border bg-card/80 p-4 shadow-sm">
          <div className="flex h-20 items-end justify-center gap-1.5">
            {[36, 58, 42, 72, 52, 64, 38, 55, 32, 46, 28].map((height, index) => (
              <Skeleton key={index} className="w-2 rounded-full" style={{ height: `${height}%` }} />
            ))}
          </div>
        </div>
        <div className="mt-4 rounded-xl border bg-card/80 p-4 shadow-sm">
          <Skeleton className="mx-auto h-7 w-2/3" />
        </div>
        <div className="mx-auto mt-4 grid w-full max-w-[320px] shrink-0 grid-cols-3 gap-2">
          {Array.from({ length: 12 }).map((_, index) => (
            <Skeleton key={index} className="h-14 rounded-xl border border-border/50" />
          ))}
        </div>
        <div className="mx-auto mt-4 grid w-full max-w-[320px] grid-cols-3 items-center">
          <div />
          <Skeleton className="mx-auto size-14 rounded-full bg-emerald-500/15" />
          <Skeleton className="mx-auto size-10 rounded-full" />
        </div>
      </div>
    </div>
  );
}

function DialerColumnSkeleton() {
  return (
    <div className="mx-auto flex min-h-[34rem] w-full max-w-[360px] flex-col lg:mx-0 lg:min-h-0">
      <div className="grid h-12 grid-cols-2 gap-1 rounded-xl bg-muted/45 p-1">
        <Skeleton className="h-full bg-background/80 shadow-sm" />
        <Skeleton className="h-full bg-muted-foreground/10" />
      </div>
      <div className="mt-4 flex items-center gap-4 rounded-xl border bg-card/80 p-4 shadow-sm">
        <div className="min-w-0 flex-1 space-y-2">
          <Skeleton className="h-4 w-36" />
          <Skeleton className="h-3 w-44 max-w-full" />
        </div>
        <Skeleton className="h-6 w-11 shrink-0 rounded-full" />
      </div>
      <div className="mt-4 flex min-h-0 flex-1 flex-col items-center overflow-hidden rounded-2xl border bg-card/80 px-6 py-7 shadow-sm">
        <Skeleton className="size-20 rounded-full ring-4 ring-muted/70" />
        <Skeleton className="mt-5 h-6 w-40" />
        <Skeleton className="mt-2 h-3 w-28" />
        <div className="mt-4 flex gap-2">
          <Skeleton className="h-6 w-20 rounded-full" />
          <Skeleton className="h-6 w-16 rounded-full" />
        </div>
        <div className="mt-8 flex w-full flex-1 items-end justify-center">
          <Skeleton className="size-16 rounded-full bg-emerald-500/15" />
        </div>
      </div>
    </div>
  );
}

// Preserva a geometria real das telas durante o carregamento do chunk e das
// credenciais SIP, evitando o salto entre um aviso pequeno e as três colunas.
export function PhoneWorkspaceSkeleton({
  variant,
  withHeader = false,
}: {
  variant: PhoneWorkspaceVariant;
  withHeader?: boolean;
}) {
  const { t } = useTranslation();
  const label =
    variant === "dialer"
      ? t("calling.workspace.preparingDialer")
      : t("calling.workspace.preparingPhone");

  return (
    <div role="status" aria-label={label} aria-live="polite" className={cn(withHeader && "p-6")}>
      {withHeader && (
        <div className="flex items-center justify-between gap-3">
          <Skeleton className="h-5 w-44" />
          <div className="flex items-center gap-2">
            <Skeleton className="h-6 w-24 rounded-full" />
            <Skeleton className="size-9 rounded-lg" />
          </div>
        </div>
      )}

      <div className="mt-6 flex flex-col gap-6 lg:h-[calc(100vh-7.5rem)] lg:flex-row lg:items-stretch lg:overflow-hidden">
        {variant === "dialer" ? <DialerColumnSkeleton /> : <PhoneColumnSkeleton />}
        <div className="mx-auto flex w-full min-h-0 max-w-[360px] lg:mx-0 lg:max-w-none lg:flex-1">
          <ScriptPanelSkeleton />
        </div>
        <div className="mx-auto flex w-full min-h-0 max-w-[360px] lg:mx-0 lg:max-w-none lg:flex-1">
          <SessionPanelSkeleton variant={variant} />
        </div>
      </div>
    </div>
  );
}

// Aviso honesto quando o usuário não tem ramal ativo — em vez de um botão de ligar inerte.
// A senha do ramal vem do back (/v1/sip/agents/me); o retry solicita ao back o
// provisionamento do próprio usuário e exibe qualquer falha de forma acionável.
export function UnprovisionedNotice({
  notice,
  onRetry,
  retryLabel,
  retrying = false,
  showRetry = true,
  retryError = null,
}: {
  notice: string | null;
  onRetry: () => void | Promise<void>;
  retryLabel?: string;
  retrying?: boolean;
  showRetry?: boolean;
  retryError?: string | null;
}) {
  const { t } = useTranslation();
  const effectiveRetryLabel = retryLabel ?? t("calling.workspace.retry");
  return (
    <div className="mx-auto mt-6 w-full max-w-[480px] rounded-lg border border-dashed border-border p-6">
      <div className="flex items-center gap-2 text-sm font-medium">
        <CircleAlert className="size-4 text-muted-foreground" />
        {t("calling.workspace.unavailableTitle")}
      </div>
      <p className="mt-2 text-sm text-muted-foreground">
        {notice ?? t("calling.workspace.unavailableBody")}
      </p>
      {showRetry && (
        <Button size="sm" variant="outline" className="mt-4" disabled={retrying} onClick={onRetry}>
          {retrying && <Spinner className="size-4" />}
          {effectiveRetryLabel}
        </Button>
      )}
      {retryError && <p className="mt-3 text-sm text-destructive">{retryError}</p>}
    </div>
  );
}

// Aviso de que a tela pode bloquear e derrubar a ligação. Só no celular: num
// desktop, bloqueio de tela não mata o WebRTC do mesmo jeito e o aviso seria
// ruído que o vendedor aprende a ignorar.
//
// Diferente do UnprovisionedNotice, este NÃO bloqueia a tela — o vendedor
// continua conseguindo ligar. A escolha de correr o risco é dele.
const STATUS_EM_RISCO: WakeLockStatus[] = ["unsupported", "denied", "released"];

export function ScreenLockNotice({ status }: { status: WakeLockStatus }) {
  const { t } = useTranslation();
  // Fora de ligação o hook nunca chega a checar a API — devolve "idle" tanto
  // quando ela existe quanto quando não existe. Para o aviso pré-discagem
  // funcionar, tratamos "idle" sem `navigator.wakeLock` como "unsupported".
  const statusEfetivo =
    status === "idle" && !(navigator as Navigator & { wakeLock?: unknown }).wakeLock
      ? "unsupported"
      : status;

  if (!STATUS_EM_RISCO.includes(statusEfetivo)) return null;
  if (detectPlatform(navigator.userAgent, navigator.maxTouchPoints) === "desktop") return null;

  return (
    <div
      role="status"
      className="mx-auto flex w-fit max-w-[420px] items-start gap-2 rounded-lg border border-dashed border-border px-3 py-2 text-xs text-muted-foreground"
    >
      <TriangleAlert aria-hidden className="mt-0.5 size-3.5 shrink-0" />
      <span>{t("calling.workspace.screenLock")}</span>
    </div>
  );
}
